import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trash2,
  Star,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Search as SearchIcon,
  Edit3,
} from "lucide-react";
import { toast } from "sonner";
import { api, VENDOR_LABELS } from "@/lib/api";
import ProductEditModal from "@/components/admin/ProductEditModal";

// Column → key to sort by. Some columns aggregate multiple fields.
const COLS = [
  { id: "title", label: "Title", align: "left", sortable: true },
  { id: "vendor", label: "Vendor", align: "left", sortable: true },
  { id: "category_slugs", label: "Categories", align: "left", sortable: false },
  { id: "price", label: "Price", align: "right", sortable: true },
  { id: "click_count", label: "Clicks", align: "right", sortable: true },
  { id: "sort_order", label: "Order", align: "right", sortable: true },
  { id: "featured", label: "Featured", align: "center", sortable: true },
];

export default function ProductsPanel({ products, setProducts, categories, initialSlug }) {
  const [search, setSearch] = useState("");
  const [vendor, setVendor] = useState(""); // "", "amazon", "ebay", ...
  const [category, setCategory] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sortKey, setSortKey] = useState("title");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);

  // Deep-link: open the edit modal for ?slug=… once products are loaded.
  useEffect(() => {
    if (!initialSlug || products.length === 0) return;
    if (products.some((p) => p.slug === initialSlug)) {
      setEditingSlug(initialSlug);
    }
  }, [initialSlug, products]);

  // Reset selection when products list changes drastically
  useEffect(() => {
    setSelected((prev) => {
      const slugs = new Set(products.map((p) => p.slug));
      const next = new Set();
      prev.forEach((s) => slugs.has(s) && next.add(s));
      return next;
    });
  }, [products]);

  const vendors = useMemo(
    () => Array.from(new Set(products.map((p) => p.vendor))).sort(),
    [products]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = products.filter((p) => {
      if (vendor && p.vendor !== vendor) return false;
      if (category && !(p.category_slugs || []).includes(category)) return false;
      if (featuredOnly && !p.featured) return false;
      if (minPrice !== "" && Number(p.price) < Number(minPrice)) return false;
      if (maxPrice !== "" && Number(p.price) > Number(maxPrice)) return false;
      if (
        q &&
        !(
          p.title.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const at = typeof av === "string" ? av.toLowerCase() : av ?? 0;
      const bt = typeof bv === "string" ? bv.toLowerCase() : bv ?? 0;
      if (at === bt) return 0;
      const cmp = at > bt ? 1 : -1;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [products, search, vendor, category, featuredOnly, minPrice, maxPrice, sortKey, sortDir]);

  const allOnPageSelected =
    filtered.length > 0 && filtered.every((p) => selected.has(p.slug));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        filtered.forEach((p) => next.delete(p.slug));
      } else {
        filtered.forEach((p) => next.add(p.slug));
      }
      return next;
    });
  };

  const toggleOne = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  };

  const clearFilters = () => {
    setSearch("");
    setVendor("");
    setCategory("");
    setMinPrice("");
    setMaxPrice("");
    setFeaturedOnly(false);
  };

  const onHeaderClick = (col) => {
    if (!col.sortable) return;
    if (sortKey === col.id) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.id);
      setSortDir("asc");
    }
  };

  const refreshProducts = async () => {
    const { data } = await api.get("/products", { params: { page_size: 100 } });
    setProducts(data.items);
  };

  const bulk = async (action, extra = {}) => {
    if (selected.size === 0) return;
    const slugs = Array.from(selected);
    if (action === "delete" && !window.confirm(`Delete ${slugs.length} products?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk", {
        slugs,
        action,
        ...extra,
      });
      toast.success(`${action}: ${data.affected} products`);
      setSelected(new Set());
      await refreshProducts();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/products/${p.slug}`);
      setProducts((prev) => prev.filter((x) => x.slug !== p.slug));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const moveOrder = async (p, delta) => {
    const newOrder = (p.sort_order || 0) + delta;
    try {
      const { data } = await api.put(`/products/${p.slug}`, {
        ...p,
        sort_order: newOrder,
      });
      setProducts((prev) => prev.map((x) => (x.slug === p.slug ? data : x)));
    } catch {
      toast.error("Reorder failed");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      const { data } = await api.put(`/products/${p.slug}`, {
        ...p,
        featured: !p.featured,
      });
      setProducts((prev) => prev.map((x) => (x.slug === p.slug ? data : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (vendor ? 1 : 0) +
    (category ? 1 : 0) +
    (featuredOnly ? 1 : 0) +
    (minPrice !== "" ? 1 : 0) +
    (maxPrice !== "" ? 1 : 0);

  return (
    <div className="space-y-4" data-testid="admin-products-panel">
      {/* Filter bar */}
      <div className="brut bg-white p-4 space-y-3" data-testid="products-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Search
            </label>
            <div className="relative">
              <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-2.5 opacity-50" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Title, slug, description..."
                data-testid="products-search-input"
                className="w-full brut bg-white pl-8 pr-3 py-2 focus:outline-none text-sm"
              />
            </div>
          </div>
          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Vendor
            </label>
            <select
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              data-testid="products-vendor-filter"
              className="brut bg-white px-2 py-2 focus:outline-none text-sm"
            >
              <option value="">All</option>
              {vendors.map((v) => (
                <option key={v} value={v}>
                  {VENDOR_LABELS[v] || v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="products-category-filter"
              className="brut bg-white px-2 py-2 focus:outline-none text-sm"
            >
              <option value="">All</option>
              {(categories || []).map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono uppercase text-[10px] tracking-widest opacity-70 block mb-1">
              Price range
            </label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="Min"
                data-testid="products-min-price"
                className="w-20 brut bg-white px-2 py-2 focus:outline-none text-sm font-mono"
              />
              <span className="opacity-50">–</span>
              <input
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Max"
                data-testid="products-max-price"
                className="w-20 brut bg-white px-2 py-2 focus:outline-none text-sm font-mono"
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer self-end py-2">
            <input
              type="checkbox"
              checked={featuredOnly}
              onChange={(e) => setFeaturedOnly(e.target.checked)}
              data-testid="products-featured-filter"
              className="w-4 h-4 border-2 border-black"
            />
            <span className="font-mono uppercase text-[10px] tracking-widest">Featured only</span>
          </label>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="products-clear-filters"
              className="brut bg-white px-3 py-2 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1.5"
            >
              <X className="w-3 h-3" />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest opacity-60">
          Showing {filtered.length} of {products.length}
        </div>
      </div>

      {/* Bulk action bar (sticks above table when selection exists) */}
      {selected.size > 0 && (
        <div
          className="brut pcs-bg-pink text-white p-3 flex flex-wrap items-center gap-3"
          data-testid="products-bulk-bar"
        >
          <div className="font-bold uppercase text-sm tracking-wide">
            {selected.size} selected
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => bulk("feature")}
              disabled={busy}
              data-testid="products-bulk-feature"
              className="brut bg-white text-black px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
            >
              Feature
            </button>
            <button
              type="button"
              onClick={() => bulk("unfeature")}
              disabled={busy}
              data-testid="products-bulk-unfeature"
              className="brut bg-white text-black px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
            >
              Unfeature
            </button>
            <button
              type="button"
              onClick={() => {
                const order = Number(window.prompt("Sort order (higher = appears first):", "100"));
                if (!Number.isNaN(order)) bulk("set-sort-order", { sort_order: order });
              }}
              disabled={busy}
              data-testid="products-bulk-set-order"
              className="brut bg-white text-black px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
            >
              Set order
            </button>
            <button
              type="button"
              onClick={() => {
                const slugs = window.prompt(
                  "Comma-separated category slugs (replaces existing):",
                  ""
                );
                if (slugs === null) return;
                bulk("set-categories", {
                  category_slugs: slugs.split(",").map((s) => s.trim()).filter(Boolean),
                });
              }}
              disabled={busy}
              data-testid="products-bulk-categories"
              className="brut bg-white text-black px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50"
            >
              Set categories
            </button>
            <button
              type="button"
              onClick={() => bulk("delete")}
              disabled={busy}
              data-testid="products-bulk-delete"
              className="brut bg-black text-white px-3 py-1.5 font-bold uppercase text-xs tracking-wide brut-press disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              data-testid="products-bulk-clear"
              className="brut bg-white text-black p-1.5 brut-press"
              aria-label="Clear selection"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="brut bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="font-mono uppercase text-[10px] tracking-widest pcs-bg-orange">
            <tr>
              <th className="p-3 border-b-2 border-black w-10">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleAll}
                  data-testid="products-select-all"
                  className="w-4 h-4 border-2 border-black"
                />
              </th>
              {COLS.map((col) => (
                <th
                  key={col.id}
                  className={`p-3 border-b-2 border-black text-${col.align} ${
                    col.sortable ? "cursor-pointer select-none" : ""
                  }`}
                  onClick={() => onHeaderClick(col)}
                  data-testid={`products-th-${col.id}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable && (
                      sortKey === col.id ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="w-3 h-3" strokeWidth={3} />
                        ) : (
                          <ArrowDown className="w-3 h-3" strokeWidth={3} />
                        )
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" strokeWidth={2.5} />
                      )
                    )}
                  </span>
                </th>
              ))}
              <th className="p-3 border-b-2 border-black"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={COLS.length + 2} className="p-6 text-center text-sm text-gray-600">
                  {products.length === 0
                    ? "No products yet — import from eBay or add manually above."
                    : "No products match the current filters."}
                </td>
              </tr>
            ) : (
              filtered.map((p) => (
                <tr
                  key={p.slug}
                  className={`border-b border-black/20 ${
                    selected.has(p.slug) ? "pcs-bg-cream" : ""
                  }`}
                  data-testid={`admin-product-row-${p.slug}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selected.has(p.slug)}
                      onChange={() => toggleOne(p.slug)}
                      data-testid={`products-select-${p.slug}`}
                      className="w-4 h-4 border-2 border-black"
                    />
                  </td>
                  <td className="p-3">
                    <Link
                      to={`/product/${p.slug}`}
                      className="font-bold hover:pcs-text-pink"
                    >
                      {p.title}
                    </Link>
                  </td>                  <td className="p-3 font-mono uppercase text-[11px]">
                    {VENDOR_LABELS[p.vendor] || p.vendor}
                  </td>
                  <td className="p-3 font-mono text-[11px]">
                    {(p.category_slugs || []).join(", ")}
                  </td>
                  <td className="p-3 text-right font-display text-base">
                    ${p.price.toFixed(2)}
                  </td>
                  <td className="p-3 text-right font-display">{p.click_count || 0}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveOrder(p, +1)}
                        data-testid={`products-order-up-${p.slug}`}
                        className="brut bg-white px-1 brut-press"
                        aria-label="Move up"
                        title="Increase sort_order"
                      >
                        <ArrowUp className="w-3 h-3" strokeWidth={3} />
                      </button>
                      <span className="font-mono text-xs w-6 text-center">
                        {p.sort_order || 0}
                      </span>
                      <button
                        type="button"
                        onClick={() => moveOrder(p, -1)}
                        data-testid={`products-order-down-${p.slug}`}
                        className="brut bg-white px-1 brut-press"
                        aria-label="Move down"
                        title="Decrease sort_order"
                      >
                        <ArrowDown className="w-3 h-3" strokeWidth={3} />
                      </button>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      type="button"
                      onClick={() => toggleFeatured(p)}
                      data-testid={`products-toggle-featured-${p.slug}`}
                      className="brut-press"
                      aria-label="Toggle featured"
                    >
                      <Star
                        className={`w-4 h-4 ${p.featured ? "pcs-text-pink" : "opacity-30"}`}
                        fill={p.featured ? "currentColor" : "none"}
                        strokeWidth={2.5}
                      />
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1 justify-end">
                      <button
                        type="button"
                        onClick={() => setEditingSlug(p.slug)}
                        data-testid={`admin-product-edit-${p.slug}`}
                        className="brut bg-white px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteOne(p)}
                        data-testid={`admin-product-delete-${p.slug}`}
                        className="font-mono uppercase text-[10px] tracking-widest brut bg-white px-2 py-1 brut-press"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingSlug && (
        <ProductEditModal
          slug={editingSlug}
          categories={categories}
          onClose={() => setEditingSlug(null)}
          onSaved={(updated) =>
            setProducts((prev) =>
              prev.map((x) => (x.slug === updated.slug ? updated : x))
            )
          }
        />
      )}
    </div>
  );
}
